/**
 * Compact payout display helpers for Overview / journey list rows.
 * Kept separate from fee-model negotiation math so cold Overview can avoid feeModelUtils.
 */

/** Creator payout (flat/hourly Stripe Checkout) has been recorded as paid. */
export const isCreatorPayoutPaid = (collaboration) => {
  if (!collaboration) return false;
  return (
    collaboration.flat_fee_payment_status === 'paid'
    || collaboration.payment_status === 'paid'
    || collaboration.payment_record_status === 'paid'
    || collaboration.payment_complete === true
    || !!collaboration.creator_paid_at
    || !!collaboration.creatorPaidAt
  );
};

export const getCollaborationSettlementStatus = (collaboration) => (
  collaboration?.payment_settlement_status
  || collaboration?.paymentSettlementStatus
  || collaboration?.settlement_status
  || collaboration?.settlementStatus
  || null
);

export const formatPaymentAmountCents = (amountCents, currency = 'usd') => {
  const cents = Number.parseInt(amountCents, 10);
  if (!Number.isFinite(cents) || cents <= 0) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
  }).format(cents / 100);
};

const getPositiveCents = (value) => {
  const cents = Number.parseInt(value, 10);
  return Number.isFinite(cents) && cents > 0 ? cents : null;
};

/**
 * Millet checkout fees have only been 3% (test/historical) or 5% (production).
 * Matching any integer bps is too loose: at 5%, a fee taken from the recorded
 * charge also reconstructs some nearby bps on `charge - fee`, so deducted
 * payouts look like brand-paid fees.
 */
const KNOWN_PLATFORM_FEE_BPS = [300, 500];

const feeMatchesKnownRate = (feeCents, baseCents) => {
  if (!feeCents || !baseCents) return false;
  return KNOWN_PLATFORM_FEE_BPS.some(
    (bps) => Math.round((baseCents * bps) / 10000) === feeCents,
  );
};

/**
 * Interpret persisted campaign payment amounts without recalculating Millet's fee.
 *
 * New checkouts store the brand total in payment_amount_cents (creator + fee)
 * and often omit creator_payout_cents on the acceptance payload. Legacy rows
 * stored the creator's negotiated charge and deducted the fee from that total.
 * Both expose creator transfer as charge minus fee; only the legacy shape
 * should be labeled as fee-deducted.
 */
export const getCollaborationPayoutAmounts = (collaboration) => {
  const paymentAmountCents = getPositiveCents(collaboration?.payment_amount_cents);
  const creatorPayoutCents = getPositiveCents(collaboration?.creator_payout_cents);
  const platformFeeCents = getPositiveCents(collaboration?.platform_fee_cents);
  const explicitBrandTotalCents = getPositiveCents(
    collaboration?.brand_total_cents ?? collaboration?.payment_total_cents,
  );
  const brandTotalCents = explicitBrandTotalCents || paymentAmountCents;

  if (creatorPayoutCents) {
    return {
      creatorPayoutCents,
      platformFeeCents,
      brandTotalCents,
      usesLegacyFeeDeduction: false,
    };
  }

  const canDeriveCreatorFromCharge = Boolean(
    paymentAmountCents
    && platformFeeCents
    && platformFeeCents < paymentAmountCents,
  );
  const usesLegacyFeeDeduction = Boolean(
    canDeriveCreatorFromCharge
    && feeMatchesKnownRate(platformFeeCents, paymentAmountCents)
    && !feeMatchesKnownRate(platformFeeCents, paymentAmountCents - platformFeeCents),
  );

  return {
    creatorPayoutCents: canDeriveCreatorFromCharge
      ? paymentAmountCents - platformFeeCents
      : paymentAmountCents,
    platformFeeCents,
    brandTotalCents,
    usesLegacyFeeDeduction,
  };
};

/** e.g. "5 payouts across 3 campaigns" */
export const getPayoutSummaryLine = (paymentCount, campaignCount) => {
  const payoutWord = paymentCount === 1 ? 'payout' : 'payouts';
  const campaignWord = campaignCount === 1 ? 'campaign' : 'campaigns';
  return `${paymentCount} ${payoutWord} across ${campaignCount} ${campaignWord}`;
};

/**
 * Compact payout label for completed Collaboration Journey rows.
 * @param {object} collaboration
 * @param {'brand'|'influencer'} [role='brand']
 * @returns {string|null} e.g. "Paid $250" / "Got paid $250", or null when unpaid / gift-only
 */
export const getCompletedPayoutDisplay = (collaboration, role = 'brand') => {
  if (!collaboration) return null;
  const paid = isCreatorPayoutPaid(collaboration)
    || collaboration.payment_released === true
    || collaboration.paymentReleased === true;
  if (!paid) return null;

  const prefix = role === 'influencer' ? 'Got paid' : 'Paid';
  const payoutAmounts = getCollaborationPayoutAmounts(collaboration);
  const roleAmountCents = role === 'influencer'
    ? payoutAmounts.creatorPayoutCents
    : payoutAmounts.brandTotalCents;
  const amountLabel = formatPaymentAmountCents(
    roleAmountCents,
    collaboration.payment_currency,
  );
  if (amountLabel) return `${prefix} ${amountLabel}`;
  return prefix;
};
